import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/tokenUtils';
import PayrollLayout from '../../layouts/PayrollLayout';

export default function ApprovalWorkflow() {
  const API_URL = "https://hrmspayroll.prepstripe.com/api";
  const token = getToken();

  const api = useMemo(() => {
    return axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // Filters
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [employeeId, setEmployeeId] = useState('');
  const [status, setStatus] = useState('processed'); // Default to processed status for approvals

  // Modal states
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [remarks, setRemarks] = useState('');

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const statusOptions = [
    { value: 'processed', label: 'Processed (Pending Approval)' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        year: parseInt(year),
        month: parseInt(month),
        per_page: perPage,
        page,
      };

      if (employeeId.trim()) params.employee_id = employeeId.trim();
      if (status) params.status = status;

      const response = await api.get('/payroll/monthly-payrolls', { params });
      const data = response.data;
      
      setRecords(Array.isArray(data) ? data : data.data || []);
      setTotal(data.meta?.total || data.length || 0);
    } catch (err) {
      console.error('Error loading records:', err);
      setError(err.response?.data?.message || 'Failed to load payroll records');
    } finally {
      setLoading(false);
    }
  }, [api, year, month, employeeId, status, perPage, page]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const openApprovalModal = (record) => {
    setSelectedRecord(record);
    setRemarks('');
    setShowApprovalModal(true);
  };

  const closeApprovalModal = () => {
    setShowApprovalModal(false);
    setSelectedRecord(null);
    setRemarks('');
  };

  const approveRecord = async (action) => {
    if (!selectedRecord) return;

    try {
      const newStatus = action === 'approve' ? 'approved' : 'rejected';
      
      await api.put(`/payroll/monthly-payrolls/${selectedRecord.id}/status`, {
        status: newStatus,
        remarks: remarks.trim() || undefined,
      });

      setSuccess(`Payroll ${action === 'approve' ? 'approved' : 'rejected'} successfully!`);
      closeApprovalModal();
      await loadRecords(); 
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.response?.data?.message || `Failed to ${action} payroll`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300', label: 'Draft' },
      processed: { bg: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400', label: 'Processed' },
      approved: { bg: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400', label: 'Approved' },
      rejected: { bg: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400', label: 'Rejected' },
      paid: { bg: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400', label: 'Paid' },
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${config.bg}`}>
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <PayrollLayout title="Approval Workflow">
      <div className="flex flex-col gap-y-3 font-poppins">
        
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 p-4 mx-2 rounded-2xl shadow">
          <h2 className="text-xl font-semibold dark:text-white mb-4">Payroll Approval Workflow</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Review and approve processed payroll records. You can approve or reject payroll records with remarks.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-900 p-4 mx-2 rounded-2xl shadow">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium dark:text-white">Year</label>
              <input
                type="number"
                className="border rounded px-3 py-2 dark:bg-black dark:text-white"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                min="2020"
                max="2030"
              />
            </div>
            
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium dark:text-white">Month</label>
              <select
                className="border rounded px-3 py-2 dark:bg-black dark:text-white"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium dark:text-white">Employee ID</label>
              <input
                type="text"
                className="border rounded px-3 py-2 dark:bg-black dark:text-white"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                placeholder="Filter by employee"
              />
            </div>
            
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium dark:text-white">Status</label>
              <select
                className="border rounded px-3 py-2 dark:bg-black dark:text-white"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium dark:text-white">Per Page</label>
              <input
                type="number"
                className="border rounded px-3 py-2 dark:bg-black dark:text-white"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value) || 20)}
                min="1"
                max="100"
              />
            </div>
          </div>
          
          <div className="mt-4 flex gap-3">
            <button
              onClick={() => { setPage(1); loadRecords(); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Apply Filters
            </button>
            <button
              onClick={() => {
                setYear(new Date().getFullYear().toString());
                setMonth((new Date().getMonth() + 1).toString().padStart(2, '0'));
                setEmployeeId('');
                setStatus('processed');
                setPerPage(20);
                setPage(1);
                loadRecords();
              }}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-700"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Records Table */}
        <div className="bg-white dark:bg-gray-900 p-0 mx-2 rounded-2xl shadow overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="text-left px-3 py-2 font-semibold dark:text-white">Employee</th>
                <th className="text-left px-3 py-2 font-semibold dark:text-white">Month/Year</th>
                <th className="text-left px-3 py-2 font-semibold dark:text-white">Basic Salary</th>
                <th className="text-left px-3 py-2 font-semibold dark:text-white">Gross Salary</th>
                <th className="text-left px-3 py-2 font-semibold dark:text-white">Net Pay</th>
                <th className="text-left px-3 py-2 font-semibold dark:text-white">Status</th>
                <th className="text-left px-3 py-2 font-semibold dark:text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-3 text-center dark:text-white">
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-3 text-center dark:text-white">
                    No records found
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record.id} className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-3 py-2 dark:text-white">
                      <div>
                        <div className="font-medium">{record.employee_name || record.employee_id}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{record.employee_id}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 dark:text-white">
                      {months.find(m => m.value === record.month?.toString().padStart(2, '0'))?.label} {record.year}
                    </td>
                    <td className="px-3 py-2 dark:text-white">{formatCurrency(record.basic_salary)}</td>
                    <td className="px-3 py-2 dark:text-white">{formatCurrency(record.gross_salary)}</td>
                    <td className="px-3 py-2 dark:text-white">{formatCurrency(record.net_pay)}</td>
                    <td className="px-3 py-2">{getStatusBadge(record.status)}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        {record.status === 'processed' && (
                          <>
                            <button
                              onClick={() => openApprovalModal(record)}
                              className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                            >
                              Review
                            </button>
                            <button
                              onClick={() => window.open(`/api/payroll/monthly-payrolls/${record.id}/download`, '_blank')}
                              className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                            >
                              Download
                            </button>
                          </>
                        )}
                        {record.status === 'approved' && (
                          <span className="text-green-600 dark:text-green-400 text-xs">✓ Approved</span>
                        )}
                        {record.status === 'rejected' && (
                          <span className="text-red-600 dark:text-red-400 text-xs">✗ Rejected</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mx-2">
          <div className="text-sm dark:text-white">
            Page {page} {total ? `of ${Math.ceil(total / perPage)}` : ''} 
            {total > 0 && ` (${total} total records)`}
          </div>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-3 py-1 bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-white rounded"
            >
              Previous
            </button>
            <button
              disabled={total && page >= Math.ceil(total / perPage)}
              onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-white rounded"
            >
              Next
            </button>
          </div>
        </div>

        {/* Approval Modal */}
        {showApprovalModal && selectedRecord && (
          <div className="fixed inset-0 z-[1001] flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold dark:text-white mb-4">
                Review Payroll - {selectedRecord.employee_name || selectedRecord.employee_id}
              </h3>
              
              <div className="space-y-3 mb-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-medium dark:text-white">Basic Salary:</span></div>
                  <div className="dark:text-white">{formatCurrency(selectedRecord.basic_salary)}</div>
                  
                  <div><span className="font-medium dark:text-white">Gross Salary:</span></div>
                  <div className="dark:text-white">{formatCurrency(selectedRecord.gross_salary)}</div>
                  
                  <div><span className="font-medium dark:text-white">Net Pay:</span></div>
                  <div className="dark:text-white">{formatCurrency(selectedRecord.net_pay)}</div>
                  
                  <div><span className="font-medium dark:text-white">Month/Year:</span></div>
                  <div className="dark:text-white">
                    {months.find(m => m.value === selectedRecord.month?.toString().padStart(2, '0'))?.label} {selectedRecord.year}
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium dark:text-white mb-2">
                  Remarks (Optional)
                </label>
                <textarea
                  className="w-full border rounded px-3 py-2 dark:bg-black dark:text-white"
                  rows={3}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Add any remarks for approval/rejection..."
                />
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeApprovalModal}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => approveRecord('reject')}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={() => approveRecord('approve')}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mx-2">
            {success}
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mx-2">
            {error}
          </div>
        )}
      </div>
    </PayrollLayout>
  );
}
